import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  Avatar,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Badge,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { gql, useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import emptyCart from "../../assets/Icons/emptyCart.svg";
import useRemoveCarts from "../../Hooks/useRemoveCart";
import useGetServiceCart from "../../Hooks/useGetServiceCart";
import useGetCart from "../../Hooks/useGetCart";
import useIncreaseCarts from "../../Hooks/useIncrease";
import { SlRefresh } from "react-icons/sl";
import useAuthFlow from "../../Hooks/use-auth-flow";

const GET_CART_ITEMS = gql`
  query GetCartItems {
    cartItems @client
  }
`;

const GET_SERVICE_CART = gql`
  query GetServiceItems {
    serviceCarts @client
  }
`;

// Utility function to format dates
function formatDate(dateString) {
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", options);
}

const CartDrawer = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tabValue, setTabValue] = useState(0);
     const { triggerAuth } = useAuthFlow();
  
  // Authentication check
  const isAuthenticated = localStorage.getItem("isAuthenticated") === "true";
  const canProceed = Boolean(isAuthenticated);

  // Queries
  const { loading, data, refetch } = useQuery(GET_CART_ITEMS, {
    fetchPolicy: "cache-first",
    errorPolicy: "ignore",
  });
  const { data: serviceCart, refetch: refetchService } = useQuery(
    GET_SERVICE_CART,
    {
      fetchPolicy: "cache-first",
      errorPolicy: "ignore",
    }
  );

  // Hooks - memoized to prevent re-creation
  const { handleGetCart } = useGetCart();
  const { handleGetCart: handleServiceCart } = useGetServiceCart();
  const { handleRemoveCart, isLoading: removing } = useRemoveCarts();
  const { handleIncreaseCart } = useIncreaseCarts();

  // Memoized cart items to prevent unnecessary re-renders
  const productCartItems = useMemo(
    () => data?.cartItems || [],
    [data?.cartItems]
  );
  const serviceCartItems = useMemo(
    () => serviceCart?.serviceCarts || [],
    [serviceCart?.serviceCarts]
  );

  // Initialize cart data only when drawer opens
  useEffect(() => {
    if (open) {
      // Add a small delay to prevent blocking UI
      const timeoutId = setTimeout(() => {
        handleGetCart();
        handleServiceCart();
        refetch();
        refetchService();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  // Memoized calculations to prevent unnecessary re-computations
  const totals = useMemo(() => {
    const productTotal = productCartItems.reduce(
      (total, item) => {
        // Use totalAmount for products (like CustomTable) or totalPrice as fallback
        const itemTotal = item.totalAmount || item.totalPrice || 0;
        return total + itemTotal;
      },
      0
    );

    const serviceTotal = serviceCartItems.reduce(
      (total, item) => {
        // Use totalPrice for services (like Cart.js)
        const itemTotal = item.totalPrice || item.totalAmount || 0;
        return total + itemTotal;
      },
      0
    );

 

    return {
      productTotal,
      serviceTotal,
      grandTotal: productTotal + serviceTotal,
      totalItems: productCartItems.length + serviceCartItems.length,
    };
  }, [productCartItems, serviceCartItems]);

  // Memoized handlers to prevent re-creation on every render
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleRemoveItem = useCallback(
    (itemId) => {
      handleRemoveCart(itemId);
      // Use a more efficient refresh approach
      setTimeout(() => {
        refetch();
        refetchService();
      }, 300);
    },
    [handleRemoveCart, refetch, refetchService]
  );

  const handleQuantityIncrease = useCallback(
    (item) => {
      const newQuantity = item.quantity + 1;
      handleIncreaseCart(item?.product?._id, newQuantity);
      setTimeout(() => {
        handleGetCart();
        refetch();
      }, 300);
    },
    [handleIncreaseCart, handleGetCart, refetch]
  );

  const handleQuantityDecrease = useCallback(
    (item) => {
      if (item.quantity > 1) {
        const newQuantity = item.quantity - 1;
        handleIncreaseCart(item?.product?._id, newQuantity);
        setTimeout(() => {
          handleGetCart();
          refetch();
        }, 300);
      }
    },
    [handleIncreaseCart, handleGetCart, refetch]
  );

  const handleCheckout = useCallback(() => {
    onClose();
    if (canProceed) {
      if (tabValue === 0) {
        navigate("/checkout");
      } else {
        navigate("/service/checkout");
      }
    } else {
triggerAuth("signin")
    }
  }, [onClose, tabValue, navigate, canProceed]);

  const handleViewFullCart = useCallback(() => {
    onClose();
    navigate(tabValue === 0 ? "/cart" : "/cart?tab=service");
  }, [onClose, tabValue, navigate]);

  const handleContinueShopping = useCallback(() => {
    onClose();
    navigate("/");
  }, [onClose, navigate]);

  // Memoized cart item renderer to prevent unnecessary re-renders
  const renderCartItem = useCallback(
    (item, isService = false) => (
      <ListItem
        key={item._id || item.id}
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
          border: "1px solid #f0f0f0",
          borderRadius: 2,
          mb: 1,
          p: 2,
        }}
      >
        <Box sx={{ display: "flex", width: "100%", alignItems: "flex-start" }}>
          <Avatar
            src={isService ? item?.service?.images?.[0] : item?.product?.images?.[0] || item.image}
            alt={isService ? item?.service?.title : item?.product?.name || item.title}
            sx={{
              width: 60,
              height: 60,
              mr: 2,
              borderRadius: 1,
            }}
            variant="rounded"
          />

          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              className="one-line-ellipsis"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                fontSize: "14px",
              }}
            >
              {isService ? item?.service?.title : item?.product?.name || item.title}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: "12px" }}
            >
              ${parseFloat(item.pricePerUnit || 0).toFixed(2)}
            </Typography>

            {isService && item?.scheduledDate && item?.scheduledTime && (
              <Typography
                variant="body2"
                sx={{ fontSize: "10px", fontWeight: 700, mb: 1, color: "#666" }}
              >
                {formatDate(item.scheduledDate)} / {item.scheduledTime}
              </Typography>
            )}

            {!isService && (
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => handleQuantityDecrease(item)}
                  disabled={item.quantity <= 1}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    width: 30,
                    height: 30,
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>

                <Typography sx={{ mx: 2, minWidth: 20, textAlign: "center" }}>
                  {item.quantity}
                </Typography>

                <IconButton
                  size="small"
                  onClick={() => handleQuantityIncrease(item)}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    width: 30,
                    height: 30,
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#5C3069" }}
              >
                Total: ${parseFloat(isService ? (item.totalPrice || 0) : (item.totalAmount || item.totalPrice || 0)).toFixed(2)}
              </Typography>

              <IconButton
                size="small"
                onClick={() => handleRemoveItem(item._id || item.id)}
                disabled={removing}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </ListItem>
    ),
    [handleQuantityDecrease, handleQuantityIncrease, handleRemoveItem, removing]
  );

  // Refresh cart function
  const handleRefreshCart = useCallback(() => {
    handleGetCart();
    handleServiceCart();
    refetch();
    refetchService();
  }, [handleGetCart, handleServiceCart, refetch, refetchService]);

  // Empty cart component
  const EmptyCart = ({ message }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        textAlign: "center",
        minHeight: "40vh",
      }}
    >
      <img
        src={emptyCart}
        alt="Empty cart"
        width={80}
        style={{ marginBottom: 16 }}
      />
      <Typography variant="h6" sx={{ mb: 1, fontSize: "16px" }}>
        {message}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Browse our products and add items to your cart
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", maxWidth: "200px" }}>
        <Button
          variant="contained"
          onClick={handleRefreshCart}
          startIcon={<SlRefresh />}
          sx={{
            backgroundColor: "#5C3069",
            "&:hover": {
              backgroundColor: "#4a2555",
            },
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Refresh Cart
        </Button>
        <Button
          variant="outlined"
          onClick={handleContinueShopping}
          sx={{
            borderColor: "#5C3069",
            color: "#5C3069",
            "&:hover": {
              borderColor: "#4a2555",
              backgroundColor: "rgba(92, 48, 105, 0.04)",
            },
            borderRadius: "8px",
            px: 3,
            py: 1,
          }}
        >
          Continue Shopping
        </Button>
      </Box>
    </Box>
  );



  return (
    <>
      {/* Floating close button outside drawer */}
      {open && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "fixed",
            top: 16,
            left: isMobile ? 16 : "calc(100vw - 450px - 60px)",
            zIndex: 1300, // Higher than drawer's z-index
            backgroundColor: "#fff",
            color: "white",
            width: 40,
            height: 40,
            "&:hover": {
              backgroundColor: "rgba(244, 238, 238, 0.9)",
            },
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          <CloseIcon fontSize="small" sx={{ color: "#000" }} />
        </IconButton>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: isMobile ? "100vw" : 450,
            maxWidth: "100vw",
            borderRadius: "21px",
            m: 1,
            pb: 1,
            bgcolor: "transparent",
            boxShadow:'none'
          },
        }}
      >
        <Box
          sx={{
            height: "98%",
            display: "flex",
            bgcolor: "#fff",
            flexDirection: "column",
            borderRadius: "21px",
            overflow: "hidden",
            boxShadow:4
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, fontSize: "24px" }}
              >
                My Cart
              </Typography>
              <Badge
                badgeContent={totals.totalItems}
                color="error"
                sx={{ ml: 1 }}
              />
            </Box>
            {/* Order  */}
          </Box>

          {/* Tabs */}
          {(productCartItems.length > 0 || serviceCartItems.length > 0) && (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
              >
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Products
                      <Chip
                        label={productCartItems.length}
                        size="small"
                        sx={{ ml: 1, height: 20 }}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Services
                      <Chip
                        label={serviceCartItems.length}
                        size="small"
                        sx={{ ml: 1, height: 20 }}
                      />
                    </Box>
                  }
                />
              </Tabs>
            </Box>
          )}

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography>Loading cart...</Typography>
              </Box>
            ) : (
              <>
                {/* Products Tab */}
                {tabValue === 0 && (
                  <>
                    {productCartItems.length === 0 ? (
                      <EmptyCart message="Your product cart is empty" />
                    ) : (
                      <List sx={{ p: 0 }}>
                        {productCartItems.map((item) =>
                          renderCartItem(item, false)
                        )}
                      </List>
                    )}
                  </>
                )}

                {/* Services Tab */}
                {tabValue === 1 && (
                  <>
                    {serviceCartItems.length === 0 ? (
                      <EmptyCart message="Your service cart is empty" />
                    ) : (
                      <List sx={{ p: 0 }}>
                        {serviceCartItems.map((item) =>
                          renderCartItem(item, true)
                        )}
                      </List>
                    )}
                  </>
                )}
              </>
            )}
          </Box>

          {/* Footer with totals and actions */}
          {totals.totalItems > 0 && (
            <Box
              sx={{
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#f9f9f9",
                p: 2,
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2">
                    {tabValue === 0 ? "Products" : "Services"} Total:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    $
                    {(tabValue === 0
                      ? totals.productTotal
                      : totals.serviceTotal
                    ).toFixed(2)}
                  </Typography>
                </Box>

                {productCartItems.length > 0 && serviceCartItems.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Grand Total:
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#5C3069" }}
                    >
                      ${totals.grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleCheckout}
                  disabled={
                    (tabValue === 0 && productCartItems.length === 0) ||
                    (tabValue === 1 && serviceCartItems.length === 0)
                  }
                  sx={{
                    backgroundColor: "#5C3069",
                    "&:hover": {
                      backgroundColor: "#4a2555",
                    },
                    borderRadius:'50px',
                    height:'46px',
                    py: 1.5,
                  }}
                >
                  {isAuthenticated ? "Proceed to Checkout" : "Sign in to proceed"}
                </Button>
                
                {!isAuthenticated && (
                  <Typography
                    sx={{ textAlign: "center", fontSize: "12px", mt: 1 }}
                  >
                    New to Mamaket?{" "}
                    <Link to="/register" onClick={onClose} style={{ textDecoration: "none", color: "#5C3069" }}>Sign up </Link>
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleViewFullCart}
                  sx={{
                    borderColor: "#5C3069",
                    color: "#5C3069",
                    borderRadius:'50px',
                    height:'46px',
                    "&:hover": {
                      borderColor: "#4a2555",
                      backgroundColor: "rgba(92, 48, 105, 0.04)",
                    },
                  }}
                >
                  View Full Cart
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default CartDrawer;
